import React, { memo, useMemo } from 'react'
import { FieldErrors, SubmitHandler, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import { Button } from 'transfix-ui/components/Button'
import Box from 'transfix-ui/core/Box'
import { TEST_IDS } from '../../constants'
import { useSavedLaneFormContext } from '../../context/SavedLaneFormContext'
import { TSavedLaneSchema } from '../../schema'
import { TLaneSchema } from '../../schema/LaneSchema'
import { IAppTypes, IEquipmentTypes } from '../../types'
import AverageMarketRateMessage from '../AverageMarketRateMessage'
import CarrierApplicationNameTitle from '../CarrierApplicationNameTitle'
import ControlledSelectDateType from '../ControlledSelectDateType'
import ControlledSelectEquipment from '../ControlledSelectEquipment'
import ControlledSwitchReceiveNotifications from '../ControlledSwitchReceiveNotifications'
import DateFormSection from '../DateFormSection'
import MotorCarrierFormSection from '../MotorCarrierFormSection'
import PickupDropoffFormSection from '../PickupDropoffFormSection'
import RateFormSection from '../RateFormSection'
import RecipientsFormSection from '../RecipientsFormSection'
import { useSavedLaneFormBodyStyles } from './useSavedLaneFormBodyStyles'

export interface ISavedLaneFormBodyProps {
  /**
   * @default 'marketplace'
   */
  app: IAppTypes
  /**
   * @default false
   */
  loading?: boolean
  carrierApplicationId?: string
  onSubmit: SubmitHandler<TSavedLaneSchema>
  onError?: (props: { errors: FieldErrors<TSavedLaneSchema>; formValues: TSavedLaneSchema }) => void
  onCancel?: () => void
}

const SavedLaneFormBody = memo<ISavedLaneFormBodyProps>(
  ({
    app = IAppTypes.marketplace,
    loading = false,
    carrierApplicationId,
    onCancel,
    onSubmit,
    onError,
  }) => {
    const classes = useSavedLaneFormBodyStyles()
    /**
     * Form Context
     */
    const { control, getValues, handleSubmit } = useSavedLaneFormContext()

    const lane = useWatch<TLaneSchema>({ control, name: 'lane' })
    const equipment = useWatch<IEquipmentTypes>({ control, name: 'equipment' })

    const submitHandler = useMemo(() => {
      if (onError) {
        return handleSubmit(onSubmit, errors => {
          onError({ errors, formValues: getValues() })
        })
      }
      return handleSubmit(onSubmit)
    }, [getValues, handleSubmit, onError, onSubmit])

    return (
      <div>
        {app === IAppTypes.marketplace && carrierApplicationId && (
          <CarrierApplicationNameTitle carrierApplicationId={carrierApplicationId} />
        )}
        <Box mb={3}>
          <Box mb={2}>
            <PickupDropoffFormSection />
          </Box>
          <Box display='flex' alignItems='center'>
            <Box mr={1}>
              <ControlledSelectDateType control={control} name='dateType' />
            </Box>
            <div>
              <ControlledSelectEquipment control={control} name='equipment' />
            </div>
          </Box>
        </Box>
        <Box mb={3}>
          <RateFormSection />
          <AverageMarketRateMessage lane={lane} equipment={equipment} />
        </Box>
        <div>
          <DateFormSection />
        </div>
        {app === IAppTypes.fms && (
          <Box mb={3} data-test={TEST_IDS.motorCarrierFormSection}>
            <MotorCarrierFormSection />
          </Box>
        )}
        {carrierApplicationId && app === IAppTypes.marketplace && (
          <Box mb={3} data-test={TEST_IDS.recipientsFormSection}>
            <RecipientsFormSection carrierApplicationId={carrierApplicationId} />
          </Box>
        )}
        <div className={classes.buttonWrapper}>
          <div className={classes.switchWrapper}>
            {app === IAppTypes.fms && (
              <ControlledSwitchReceiveNotifications control={control} name='receiveNotifications' />
            )}
          </div>
          <div className={classNames(classes.buttons, 'modal-footer')}>
            {onCancel && (
              <Box mr={1}>
                <Button
                  type='button'
                  color='tertiary'
                  data-test={TEST_IDS.cancelButton}
                  onClick={onCancel}
                  size='large'
                  disabled={loading}>
                  Cancel
                </Button>
              </Box>
            )}
            <Box>
              <Button
                type='submit'
                data-test={TEST_IDS.submitButton}
                onClick={submitHandler}
                size='large'
                disabled={loading}>
                Save
              </Button>
            </Box>
          </div>
        </div>
      </div>
    )
  }
)

export default SavedLaneFormBody
